import { MailIcon } from "@heroicons/react/outline";
import { Field, FormikHelpers, FormikProps } from "formik";
import { capitalize, isEmpty } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import * as Yup from "yup";
import CenteredLoading from "../../../../components/CenteredLoading";
import DropdownSelect from "../../../../components/forms/DropdownSelect";
import FormProvider from "../../../../components/forms/FormProvider";
import HandleFieldChange from "../../../../components/forms/HandleFieldChange";
import { useAuthentication } from "../../../../context/authentication";
import { useBillingPlans } from "../../../../context/billingPlans/BillingPlansProvider";
import { useMembers } from "../../../../context/members";
import MemberAvatarImg from "../../../../context/members/MemberAvatarImg";
import { useToast } from "../../../../context/toast";
import { useWorkspaces } from "../../../../context/workspaces";
import { Role } from "../../../../utils/constants";
import { Member, Permission, PlanType } from "../../../../utils/types";
import AddMemberSidebar from "./AddMember";
import MemberActionsButton from "./MemberActionsButton";

interface Props extends RouteComponentProps<{}> {}

const MembersPage: React.FC<Props> = () => {
  const { fetchMembers, members, updateMember, isCurrentUserAdmin } =
    useMembers();
  const [openAddMemberSidebar, setOpenAddMemberSidebar] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { user } = useAuthentication();
  const { setModalUpgradeConfig } = useToast();
  const { currentWorkspace } = useWorkspaces();
  const { setNewPlan, getPlanByType } = useBillingPlans();

  const activeMembers = useMemo(
    () => members.filter((member) => !member.deleted_at),
    [members]
  );

  const toggleAddMemberFormSidebar = () => {
    if (
      currentWorkspace?.active_plan.type === PlanType.community ||
      currentWorkspace?.active_plan.type === PlanType.pro
    ) {
      setModalUpgradeConfig({
        open: true,
        resolve: () => {
          setNewPlan((prevState) => ({
            ...prevState,
            plan: getPlanByType(PlanType.business)
          }));
        },
        nextPlan: getPlanByType(PlanType.business),
        featureTitle: "Add team members",
        featureDescription:
          "This feature is only available in workspaces with a business plan or higher. Upgrade your plan to unlock this feature."
      });

      return;
    }

    setOpenAddMemberSidebar((prevValue) => !prevValue);
  };

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const filteredMembers = useMemo(() => {
    if (!searchInput) {
      return activeMembers;
    }

    return activeMembers.filter(
      (member: Member) =>
        member.name?.toLowerCase().indexOf(searchInput.toLowerCase()) > -1 ||
        member.email?.toLowerCase().indexOf(searchInput.toLowerCase()) > -1
    );
  }, [searchInput, activeMembers]);

  const getOnSubmit = useCallback(
    (member: Member) =>
      async (
        { permission }: FormValues,
        formikHelpers: FormikHelpers<FormValues>
      ) => {
        setErrorMessage(null);

        await updateMember(member, permission.label);

        formikHelpers.setSubmitting(false);
      },
    [updateMember]
  );

  return (
    <>
      <AddMemberSidebar
        open={openAddMemberSidebar}
        toggleAddMemberForm={toggleAddMemberFormSidebar}
      />

      <main className="flex flex-col w-full pb-26">
        <div className="w-full space-y-6">
          <div>
            <h1 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
              Workspace members
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-300">
              Manage who has access to your workspace
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between space-y-1">
              <div className="w-8/12">
                <div className="relative w-full text-gray-400 focus-within:text-gray-600">
                  <div
                    className="absolute inset-y-0 left-0 flex items-center pointer-events-none"
                    aria-hidden="true"
                  >
                    <svg
                      className="h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <input
                    id="search_field"
                    className="block w-full h-full pl-8 pr-3 py-2 border-transparent text-gray-900 dark:text-white dark:bg-transparent placeholder-gray-500 focus:outline-none focus:ring-0 focus:border-transparent sm:text-sm"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Filter by email or name..."
                    type="search"
                  />
                </div>
              </div>

              {isCurrentUserAdmin && (
                <button
                  onClick={toggleAddMemberFormSidebar}
                  type="button"
                  className="inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-hotspotty-400"
                >
                  New member
                </button>
              )}
            </div>

            <div>
              {isEmpty(members) ? (
                <CenteredLoading className="h-36" />
              ) : isEmpty(filteredMembers) ? (
                <p data-testid="emptyTest" className="py-3">
                  No members match your search...
                </p>
              ) : (
                <ul className="divide-y dark:divide-gray-500">
                  {filteredMembers.map((member) => (
                    <li
                      key={member.uuid}
                      className="flex items-center justify-between w-full py-4"
                    >
                      <div className="flex-1 flex items-center">
                        <div className="mr-3 hidden sm:block">
                          {member.pending ? (
                            <MailIcon className="w-10 h-10 text-gray-500 dark:text-gray-300" />
                          ) : (
                            <MemberAvatarImg member={member} sizeInPx={40} />
                          )}
                        </div>
                        <div className="flex-1 flex flex-col">
                          <span
                            data-testid="email"
                            className="text-xs md:text-sm font-semibold text-gray-900 truncate dark:text-white"
                          >
                            {member.email}
                          </span>
                          <span
                            data-testid="name"
                            className="text-xs md:text-sm text-gray-500 truncate dark:text-gray-300"
                          >
                            {renderMemberSubtitle(member)}
                          </span>
                          <span className="text-xs sm:hidden text-gray-500 truncate dark:text-gray-300">
                            {capitalize(member.role)}
                          </span>
                        </div>
                      </div>

                      <div className="hidden sm:block w-32">
                        <FormProvider
                          error={errorMessage}
                          formikProps={{
                            onSubmit: getOnSubmit(member),
                            initialValues: {
                              permission: permissions[member.role]
                            },
                            validationSchema: Yup.object().shape({
                              permission: Yup.object().required("Required")
                            })
                          }}
                          orderedFieldNames={ORDERED_FIELD_NAMES}
                          withoutSubmitButton
                        >
                          {({
                            isSubmitting,
                            submitForm
                          }: FormikProps<FormValues>) => (
                            <>
                              <Field
                                className="-mt-1"
                                name="permission"
                                placeholder="Select a permission"
                                disabled={
                                  isSubmitting ||
                                  !isCurrentUserAdmin ||
                                  member.email === user?.email ||
                                  member.pending
                                }
                                items={Object.values(permissions)}
                                itemToString={(e: Permission | null) =>
                                  e?.label ?? "member"
                                }
                                optionToString={(e: Permission | null) =>
                                  renderPermissionOption(e)
                                }
                                component={DropdownSelect}
                              />
                              <HandleFieldChange<FormValues>
                                name="permission"
                                onChange={submitForm}
                              />
                            </>
                          )}
                        </FormProvider>
                      </div>

                      <div className="flex items-center pl-4">
                        <MemberActionsButton
                          disabled={
                            !isCurrentUserAdmin || member.email === user?.email
                          }
                          member={member}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default MembersPage;

//
// Utils
//

interface PermissionGroup {
  [key: string]: Permission;
}

export const permissions: PermissionGroup = {
  admin: {
    label: Role.admin,
    description:
      "Can change workspace settings and invite new members to the workspace."
  },
  member: {
    label: Role.member,
    description:
      "Cannot change workspace settings or invite new members to the workspace."
  }
};

export const renderPermissionOption = (permission: Permission | null) => {
  if (permission === null) {
    return null;
  }

  return (
    <div className="flex flex-col">
      <span className="capitalize">{permission.label}</span>
      <span className="text-xs mt-2">{permission.description}</span>
    </div>
  );
};

interface FormValues {
  permission: Permission;
}

const ORDERED_FIELD_NAMES = ["permission"];

const renderMemberSubtitle = (member: Member) => {
  if (!member.pending) return member.name;

  return member.name
    ? `${member.name} - Awaiting response`
    : "Awaiting response";
};
